import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { ButtonLoaderComponent, SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { PdfEmbedComponent } from '@cudirect/shared-ui-library/pdf-embed';
import { CrossSellService } from '@shared/service/cross-sell.service';
import { GlobalService } from '@workflow/ecp-shared/service/global/global.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { finalize } from 'rxjs';
import { CrossSellView } from '../../../model/cross-sell.model';
import { ProductImageComponent } from '../../product-image/product-image.component';

@Component({
  selector: 'dx-cross-sell-one-click-panel',
  standalone: true,
  imports: [SharedUiLibraryModule, ProductImageComponent, CommonModule, PdfEmbedComponent, ButtonLoaderComponent],
  templateUrl: './cross-sell-one-click-panel.component.html',
  styleUrls: ['./cross-sell-one-click-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CrossSellOneClickPanelComponent {
  private readonly logger = inject(SawDust).withContext({ component: 'CrossSellOneClickPanelComponent' });
  private readonly adminPortalFeaturesService = inject(AdminPortalFeaturesService);
  private readonly globalService = inject(GlobalService);
  private readonly crossSellService = inject(CrossSellService);

  constructor() {
    // Side-effect hook (analytics/logging) when the offer changes
    effect(() => {
      const offer = this.crossSellOffer();
      if (offer) {
        // Placeholder for any side effects when a new offer arrives
        // console.debug('[CrossSellOneClickPanel] Offer changed', offer);
      }
    });
  }
  // Reactive input (nullable) - parent passes { bundle, crossSellOffer }
  public activeCrossSellOffer = input<{ bundle: any; crossSellOffer: CrossSellView; token: string } | null>(null);
  public isLoading = signal(false);
  public close = output<{ refreshed: boolean }>();

  private bundle = computed(() => this.activeCrossSellOffer()?.bundle);
  public crossSellOffer = computed(() => this.activeCrossSellOffer()?.crossSellOffer);
  // Disclosures stored as a mutable signal so we can update isOpen per item
  public disclosures = signal<any[]>([]);

  private initDisclosuresEffect = effect(() => {
    const offer = this.crossSellOffer();
    if (!offer) {
      this.disclosures.set([]);
      return;
    }
    const list = this.getDisclosures() || [];
    // Initialize first item open, others closed (mirrors pattern in workflow disclosures)
    list.forEach((d, idx) => (d.isOpen = idx === 0));
    this.disclosures.set(list);
  });

  // Computed view model derived from current offer
  productDetails = computed(() => {
    const bundle = this.bundle();
    const offer = this.crossSellOffer();
    if (!offer) {
      return {
        estimatedPayment: null as number | null,
        term: null as number | null,
        rate: null as number | null,
        amount: null as number | null,
        loanDescription: null,
        loanType: null
      };
    }
    const offerDetails = offer.offerDetails || ({} as any);
    return {
      estimatedPayment: offerDetails.estimatedPayment,
      term: offerDetails.decisionTerm,
      rate: offerDetails.decisionRate,
      amount: offerDetails.decisionAmount,
      loanDescription:
        this.getSelectedLoanProduct(bundle?.olaApplicationInformation?.loanGroupName, bundle?.olaApplicationInformation?.loanProductName)
          ?.description || '',
      loanType: offer.loanType || null
    };
  });

  private getSelectedLoanProduct(loanGroup: string, loanType: string): any {
    const consumerLoanGroups = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups;
    const selectedLoanGroup: any = consumerLoanGroups?.find((t: any) => t.loanGroup === loanGroup);
    if (!selectedLoanGroup?.loanProducts) return null;
    return selectedLoanGroup.loanProducts.find((t: any) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct);
  }

  // Optional manual reset if parent wants to blank before hiding without destroying
  reset(): void {
    // Child cannot mutate parent-owned input signal directly; parent should pass null.
    // This method can be invoked via a template ref if needed just to signal intent.
  }

  public acceptOffer(): void {
    this.isLoading.set(true);
    const productId = this.activeCrossSellOffer().crossSellOffer.productId;
    const groupId = this.activeCrossSellOffer().crossSellOffer.groupId;
    const token = this.activeCrossSellOffer().token;
    this.crossSellService
      .confirmOneClickOffer(token, groupId, productId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (success) => {
          this.logger.info('Successfully accepted one-click offer', success);
          this.close.emit({ refreshed: true });
        },
        error: (err) => {
          this.logger.error('Error accepting one-click offer', err);
        }
      });
  }

  public getDisclosures() {
    const adminDisclosures = this.adminPortalFeaturesService.config.olaConfiguration.masterDisclosureList as any;
    const crossSellDisclosures = this.crossSellOffer()?.disclosures || [];
    const selectedDisclosures = [];

    adminDisclosures.forEach((disclosure) => {
      const found = crossSellDisclosures.find((crossSellDisclosure) => crossSellDisclosure.disclosureId === disclosure.disclosureId);
      if (found) {
        // Load PDF content following the same pattern as disclosure component
        if (disclosure.isPDFFile && disclosure.pdfFileName) {
          this.globalService.downloadLegalDocument(disclosure.pdfFileName, disclosure.file?.mimeType || 'application/pdf').subscribe({
            next: (blob: Blob) => {
              disclosure.url = this.globalService.createBlobUrl(blob, disclosure.file?.mimeType || 'application/pdf');
            },
            error: (error) => {
              this.logger.error('Failed to load PDF disclosure', error);
            }
          });
        }
        selectedDisclosures.push(disclosure);
      }
    });

    return selectedDisclosures;
  }
  public toggleDisclosure(index: number): void {
    const current = this.disclosures();
    if (!current?.length) return;
    const item = current[index];
    if (!item) return;
    // Toggle clicked disclosure, close others (accordion style)
    const willOpen = !item.isOpen;
    current.forEach((d, i) => (d.isOpen = i === index ? willOpen : false));
    // Trigger signal update
    this.disclosures.set([...current]);
  }

  public disclosureTitleHtml(disclosure: any): string {
    const title = disclosure?.title || 'Disclosure';
    const arrowClass = disclosure?.isOpen ? 'cu-angle-up' : 'cu-angle-down';
    return `<h3 class=\"bl-display--flex bl-align-items--center bl-width--100 bl-margin-bottom--0\" aria-hidden=\"true\">\n        <i class=\"bl-font-size--24 bl-margin-right--16 bl-visibility--hidden cu-check-circle-solid\"></i>\n        <span class=\"bl-text-style--display-6 bl-text-align--left bl-flex--auto\">\n          <span class=\"bl-display--block\">${title}</span>\n        </span>\n        <i class=\"bl-font-size--32 ${arrowClass}\"></i>\n      </h3>`;
  }
}
